/**
 * Toast notification system for MIS-Assistant
 * Provides a simple way to show success, error, warning, and info notifications
 */

export type ToastType = 'success' | 'error' | 'warning' | 'info'

export interface Toast {
  id: string
  message: string
  type: ToastType
  duration?: number
}

class ToastManager {
  private toasts: Toast[] = []
  private listeners: Set<(toasts: Toast[]) => void> = new Set()

  add(message: string, type: ToastType, duration: number = 5000): string {
    const id = Date.now().toString(36) + Math.random().toString(36).slice(2)
    const toast: Toast = { id, message, type, duration }
    this.toasts = [...this.toasts, toast]
    this.notify()

    if (duration > 0) {
      setTimeout(() => this.remove(id), duration)
    }

    return id
  }

  remove(id: string): void {
    this.toasts = this.toasts.filter(t => t.id !== id)
    this.notify()
  }

  clear(): void {
    this.toasts = []
    this.notify()
  }

  subscribe(listener: (toasts: Toast[]) => void): () => void {
    this.listeners.add(listener)
    return () => { this.listeners.delete(listener) }
  }

  private notify(): void {
    this.listeners.forEach(listener => listener(this.toasts))
  }

  success(message: string, duration?: number): string {
    return this.add(message, 'success', duration)
  }

  error(message: string, duration?: number): string {
    return this.add(message, 'error', duration)
  }

  warning(message: string, duration?: number): string {
    return this.add(message, 'warning', duration)
  }

  info(message: string, duration?: number): string {
    return this.add(message, 'info', duration)
  }
}

// Singleton instance
export const toastManager = new ToastManager()

// Vue composable
import { readonly, ref } from 'vue'

const toasts = ref<Toast[]>([])

toastManager.subscribe((newToasts) => {
  toasts.value = newToasts
})

export function useToast() {
  return {
    toasts: readonly(toasts),
    success: (message: string, duration?: number) => toastManager.success(message, duration),
    error: (message: string, duration?: number) => toastManager.error(message, duration),
    warning: (message: string, duration?: number) => toastManager.warning(message, duration),
    info: (message: string, duration?: number) => toastManager.info(message, duration),
    remove: (id: string) => toastManager.remove(id),
    clear: () => toastManager.clear(),
  }
}
